import { useQuery } from '@apollo/client';
import { Share2 } from 'lucide-react';
import React, { useContext, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import type { GetBioacousticsSampleDataQuery } from '@/__generated__/graphql.js';
import { GetBioacousticsSampleDataDocument,GetLandCoverDocument } from '@/__generated__/graphql.js';

type BioacousticsData = GetBioacousticsSampleDataQuery['getBioacousticsSampleData'];
import { myhiveLocalization, MyHiveLocalizationKey } from '@/localization/my-hive.js';
import { LanguageContext } from '@/providers/LanguageProvider.js';

import birdSensorImage from '../../images/bird_sensor.jpg'
import csrdLogo from '../../images/CSRD_icon.svg'
import gresbLogo from '../../images/GRESB_icon.svg'
import pollinatorSensorImage from '../../images/pollinator_sensor.png'
import tnfdLogo from '../../images/TNFD_icon.svg'
import wsjLogo from '../../images/WSJ_Logo.png'
import MiniSpinner from '../asset/components/mini-spinner.js';
import { LandCoverCategory } from '../free-tool/map-layers/LandCoverMapLayer.js'; // enum — not `import type`
import { BioacousticsSearchBar } from '../free-tool/SearchBar.js';
import { PlayPauseIcon, useAudioPlayer } from './sub-components/AudioPlayer.js';
import { BioacousticsHeroTile } from './sub-components/BioacousticsHeroTile.js';
import { BioacousticsHourlyRadarChart } from './sub-components/BioacousticsHourlyRadarChart.js';
import { HillTooltip } from './sub-components/BioacousticsTooltip.js';
import {
  computeBirdStats,
  computeDateRange,
  computeMostRecentDate,
  computeTopSpecies,
  formatLocalDateTime,
} from './sub-components/bioacousticsUtils.js';
import { BirdSpeciesBar } from './sub-components/BirdSpeciesBar.js';
import { HeroChart } from './sub-components/HeroChart.js';
import { SiteLocationMap } from './sub-components/SiteLocationMap.js';


export default function BioacousticsPage({ bioacousticsData }: { bioacousticsData?: BioacousticsData } = {}): React.JSX.Element {
  const { language } = useContext(LanguageContext);
  const t = myhiveLocalization[language];

  const { data: sampleData } = useQuery(GetBioacousticsSampleDataDocument);
  const isSample = bioacousticsData == undefined
  const yearData = bioacousticsData ?? sampleData?.getBioacousticsSampleData;

  const mostRecentDate = useMemo(() => yearData ? computeMostRecentDate(yearData.dailyPollinatorData) : null, [yearData]);
  const { endDate, chartCutoff } = useMemo(() => computeDateRange(mostRecentDate), [mostRecentDate]);
  const birdStats = useMemo(() => yearData ? computeBirdStats(yearData.birdObservations) : { diversity: '—', species: '—', detections: '—' }, [yearData]);
  const topSpecies = useMemo(() => yearData ? computeTopSpecies(yearData.birdObservations) : [], [yearData]);

  const siteDetails = yearData?.siteDetails ?? null;

  const { data: landCoverData, loading: landCoverLoading } = useQuery(GetLandCoverDocument, {
    variables: { lat: siteDetails?.latitude ?? 0, lng: siteDetails?.longitude ?? 0 },
    skip: siteDetails == null,
  });

  const [hoveredLandCover, setHoveredLandCover] = useState<LandCoverCategory | null>(null);
  const { playingTaxa, handlePlay } = useAudioPlayer();

  const landCoverItems = useMemo(() => {
    const s = landCoverData?.getLandCover.summary;
    if (!s) return null;
    const total = s.naturalCover + s.agriculturalCover + s.artificialCover + s.waterCover;
    const pct = (v: number): number => Math.round((v / total) * 100);
    return [
      { name: 'Natural', pct: pct(s.naturalCover), color: '#388E3C' },
      { name: 'Agricultural', pct: pct(s.agriculturalCover), color: '#D4A017' },
      { name: 'Artificial', pct: pct(s.artificialCover), color: '#FE222A' },
      { name: 'Water', pct: pct(s.waterCover), color: '#0077C2' },
    ].filter(item => item.pct > 0);
  }, [landCoverData]);

  const landCoverLabels: Record<string, string> = {
    Natural: t[MyHiveLocalizationKey.BioacousticsPageLandCoverNatural],
    Agricultural: t[MyHiveLocalizationKey.BioacousticsPageLandCoverAgricultural],
    Artificial: t[MyHiveLocalizationKey.BioacousticsPageLandCoverArtificial],
    Water: t[MyHiveLocalizationKey.BioacousticsPageLandCoverWater],
  };

  return (
    <div className="bg-brand-soft-gray">
      <Helmet>
        <title>Real-Time Biodiversity Monitoring for Commercial Real Estate</title>
        <meta name="title" content="Real-Time Biodiversity Monitoring for Commercial Real Estate"/>
        <meta name="description" content="Real-time biodiversity monitoring for commercial real estate. Passive bioacoustic nature sensors track birds and pollinators at your asset, with year-end reporting for TNFD, GRESB RM7, and CSRD." />
        <meta property="og:title" content="Live nature readings from an office building in New York City" />
        <meta property="og:description" content="Real-time biodiversity monitoring for commercial real estate. Passive bioacoustic nature sensors track birds and pollinators at your asset, with year-end reporting for TNFD, GRESB RM7, and CSRD." />
        <meta property="og:image" content={`${import.meta.env['VITE_SSG_BASE_URL']}pollinator_sensor.png`} />
        <link rel="icon" type="image/png" href={`${import.meta.env['VITE_SSG_BASE_URL']}aura_favicon.png`} />
      </Helmet>
      {/* First screen */}
      <div className="flex flex-col" style={{ minHeight: 'calc(100vh - var(--nav-height, 0px))' }}>

        {/* Hero */}
        <div className="relative text-center pt-2 [@media(min-height:800px)]:pt-6 pb-2 shrink-0 px-4">
          {isSample &&
            <button
              onClick={() => {
                const url = window.location.href;
                if (typeof navigator.share === 'function') {
                  void navigator.share({ url });
                } else {
                  void navigator.clipboard.writeText(url);
                }
              }}
              className="absolute right-4 top-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-colors"
              style={{ backgroundColor: 'var(--color-brand-teal-dark-500)' }}
            >
              <Share2 size={12} color="white" />
              <span style={{ color: 'white' }}>{t[MyHiveLocalizationKey.Share]}</span>
            </button>
          }
          {
            isSample
              ? <h1 className="text-3xl sm:text-5xl font-inter text-gray-900 leading-tight max-w-4xl mx-auto">
                {t[MyHiveLocalizationKey.BioacousticsPageHeroTitleSamplePre]}<em>{t[MyHiveLocalizationKey.BioacousticsPageHeroTitleSampleCity]}</em>
              </h1>
              : <h1 className="text-3xl sm:text-5xl font-inter text-gray-900 leading-tight max-w-4xl mx-auto">
                {t[MyHiveLocalizationKey.BioacousticsPageHeroTitleLivePre]}{yearData?.siteDetails.name}
              </h1>
          }
        </div>

        {/* Paragraph */}
        <div className="w-full max-w-2xl mx-auto px-4 sm:px-8 pb-5 shrink-0">
          <p className="text-sm text-gray-600 leading-relaxed text-center">
            {t[MyHiveLocalizationKey.BioacousticsPageHeroDescription]}
          </p>
        </div>

        {/* Two-panel card */}
        <div className="mx-auto w-full max-w-5xl px-4 sm:px-8 pb-6 flex-1 flex flex-col">
          <div className="rounded-2xl shadow-md bg-white overflow-hidden border border-gray-100 flex-1 flex flex-col">
            <BioacousticsHeroTile data={yearData} />
          </div>
        </div>

        {/* Compliance footer */}
        <div className="max-w-5xl mx-auto px-4 sm:px-8 pb-4 flex items-center justify-center gap-6">
          <span className="text-xs uppercase tracking-widest text-gray-400">{t[MyHiveLocalizationKey.BioacousticsPageAlignedWith]}</span>
          <img src={tnfdLogo} alt="TNFD" className="h-8 w-auto" />
          <img src={gresbLogo} alt="GRESB" className="h-8 w-auto" />
          <img src={csrdLogo} alt="CSRD" className="h-8 w-auto" />
        </div>

      </div>{/* end first screen */}

      {/* 01 · Where this is happening */}
      <div hidden={!isSample} className="border-t border-gray-300 bg-brand-soft-gray">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-12 sm:py-16 flex flex-col-reverse sm:flex-row gap-8 sm:gap-12 items-start">
          {/* Map */}
          <div className="w-full sm:w-[380px] sm:shrink-0 rounded-sm overflow-hidden border border-gray-300" style={{ height: 280 }}>
            {yearData?.siteDetails ? (
              <SiteLocationMap
                siteLat={yearData.siteDetails.latitude}
                siteLng={yearData.siteDetails.longitude}
                tiff={landCoverData?.getLandCover.tiff ?? null}
                loading={landCoverLoading}
                hoveredLandCover={hoveredLandCover}
                setHoveredLandCover={setHoveredLandCover}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-xs text-gray-400 bg-brand-soft-gray">
                <MiniSpinner/>
              </div>
            )}
          </div>

          {/* Text */}
          <div className="flex-1 pt-1">
            <h2 className="text-4xl font-inter text-gray-900 mb-4">{t[MyHiveLocalizationKey.BioacousticsPageWhereTitle]}</h2>
            <p className="text-sm text-gray-600 leading-relaxed mb-8">
              {t[MyHiveLocalizationKey.BioacousticsPageWhereDescription]}
            </p>

            {/* Land cover */}
            <p className="text-xs uppercase tracking-widest text-brand-teal-dark-500 mb-3">{t[MyHiveLocalizationKey.BioacousticsPageLandCoverLabel]}</p>
            <div className="flex flex-wrap gap-2">
              {(landCoverItems ?? []).map(item => {
                const category = LandCoverCategory[item.name as keyof typeof LandCoverCategory];
                const isActive = hoveredLandCover === category;
                const isDimmed = hoveredLandCover !== null && !isActive;
                return (
                  <span
                    key={item.name}
                    className="flex items-center gap-1.5 border rounded-full px-3 py-1 text-xs cursor-pointer transition-opacity"
                    style={{
                      borderColor: isActive ? item.color : 'var(--color-brand-teal-dark-200)',
                      color: isActive ? item.color : 'var(--color-brand-teal-dark-500)',
                      opacity: isDimmed ? 0.35 : 1,
                    }}
                    onMouseEnter={() => setHoveredLandCover(category)}
                    onMouseLeave={() => setHoveredLandCover(null)}
                  >
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                    {landCoverLabels[item.name] ?? item.name}
                    <span className="font-medium">{item.pct}%</span>
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* 02 · Pollinators */}
      <div id="pollinators" className="border-t border-gray-300 bg-brand-soft-gray">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-12 sm:py-16 flex flex-col gap-6">

          {/* Heading + full-width hero chart */}
          <div>
            <div className="flex justify-between items-baseline mb-1">
              <h2 className="text-4xl font-inter text-gray-900">{t[MyHiveLocalizationKey.BioacousticsPollinators]}</h2>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed mb-3">
              {t[MyHiveLocalizationKey.BioacousticsPagePollinatorsDescription]}
            </p>
            <div className="rounded-sm shadow-sm border border-gray-200 bg-white overflow-hidden">
              <HeroChart data={yearData} startDate={chartCutoff} endDate={endDate} syncId="pollinator-section" />
            </div>
          </div>

          {/* Clock + recent buzzes side by side */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">

            {/* Left: when the buzz peaks */}
            <div className="rounded-sm border border-gray-300 p-5 bg-white">
              <p className="text-sm font-medium text-gray-700 mb-0.5">{t[MyHiveLocalizationKey.BioacousticsWhenBuzzPeaks]}</p>
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-4">{t[MyHiveLocalizationKey.BioacousticsAverageDayAllTime]}</p>
              <div style={{ height: 240 }}>
                <BioacousticsHourlyRadarChart data={yearData} series="pollinators" showPeakLabel />
              </div>
            </div>

            {/* Right: live buzzes */}
            <div className="rounded-sm border border-gray-300 p-5 flex flex-col gap-2 bg-white">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700">{t[MyHiveLocalizationKey.BioacousticsListenToRecentDetections]}</span>
                <span className="flex items-center gap-1.5 text-xs text-gray-500">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-warm-orange-500 inline-block animate-pulse" />
                  {t[MyHiveLocalizationKey.BioacousticsLive]}
                </span>
              </div>
              {!yearData ? (
                [1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="flex items-center gap-3 rounded px-3 py-2 bg-brand-teal-300/30">
                    <div className="w-7 h-7 rounded-full shrink-0 bg-brand-teal-300/50" />
                    <div className="flex-1 h-4 rounded bg-brand-teal-300/40" />
                    <div className="w-24 h-3 rounded bg-brand-teal-300/30" />
                  </div>
                ))
              ) : !yearData.buzzDetections.length ? (
                <p className="text-xs text-gray-400 text-center py-6">{t[MyHiveLocalizationKey.BioacousticsNoBuzzDetections]}</p>
              ) : (
                yearData.buzzDetections.slice(0,5).map((detection, i) => {
                  const isPlaying = playingTaxa === detection.audioUrl;
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-3 rounded px-3 py-2 transition-colors"
                      style={{ backgroundColor: isPlaying ? 'rgba(1,51,49,0.08)' : 'rgba(0,183,157,0.1)' }}
                    >
                      <button
                        onClick={() => handlePlay(detection.audioUrl, detection.audioUrl)}
                        className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center transition-colors"
                        style={{ backgroundColor: isPlaying ? 'var(--color-brand-teal-dark-500)' : 'rgba(1,51,49,0.12)' }}
                      >
                        <PlayPauseIcon isPlaying={isPlaying} playFill="rgba(1,51,49,0.75)" />
                      </button>
                      <span className="text-xs font-medium text-brand-teal-dark-500 shrink-0">{detection.hz} Hz</span>
                      <span className="flex-1 text-right text-xs text-gray-400 tabular-nums">{formatLocalDateTime(detection.dateTime)}</span>
                    </div>
                  );
                })
              )}
            </div>

          </div>

        </div>
      </div>

      {/* 03 · Birds */}
      <div id="birds" className="border-t border-gray-300 bg-gradient-to-b from-brand-teal-dark-500 to-brand-ruby">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-12 sm:py-16">

          {/* Text row */}
          <div className="flex flex-col sm:flex-row gap-6 sm:gap-12 items-start mb-10">
            <div>
              <h2 className="text-4xl font-inter" style={{ color: 'rgba(255,255,255,0.95)' }}>{t[MyHiveLocalizationKey.BioacousticsBirds]}</h2>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
                {t[MyHiveLocalizationKey.BioacousticsPageBirdsDescription]}
              </p>
            </div>
          </div>

          {yearData?.birdObservations.length && yearData.birdObservations.length > 0 ? (
            <>
            {/* Species strip — horizontal scroll */}
            <div className="flex gap-3 mb-8 overflow-x-auto pb-2">
              {topSpecies.map(entry => {
                const entryKey = entry.latinTaxa;
                const isPlaying = playingTaxa === entryKey;
                return (
                  <div
                    key={entryKey}
                    className="flex-shrink-0 w-min flex flex-col items-center rounded-sm px-3 py-5 transition-colors"
                    style={{
                      minWidth: 'calc((100% - 3.75rem) / 6)',
                      backgroundColor: isPlaying ? 'rgba(219,80,18,0.2)' : 'rgba(255,255,255,0.06)',
                      border: `1px solid ${isPlaying ? 'rgba(219,80,18,0.6)' : 'rgba(255,255,255,0.1)'}`,
                    }}
                  >
                    {/* Image with play button overlaid */}
                    <div className="relative mb-3 w-full max-w-[96px] mx-auto">
                      <div
                        className="rounded-full overflow-hidden w-full aspect-square"
                        style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}
                      >
                        {entry.imgUrl ? (
                          <img src={entry.imgUrl} alt={entry.commonName ?? entry.latinTaxa} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center" style={{ color: 'rgba(255,255,255,0.2)', fontSize: 32 }}>♦</div>
                        )}
                      </div>
                      <button
                        onClick={() => handlePlay(entryKey, entry.audioClip)}
                        className="absolute bottom-0 right-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                        style={{
                          backgroundColor: isPlaying ? 'var(--color-brand-warm-orange-500)' : 'rgba(1,51,49,0.85)',
                          border: `1.5px solid ${isPlaying ? 'var(--color-brand-warm-orange-500)' : 'rgba(255,255,255,0.4)'}`,
                          cursor: entry.audioClip ? 'pointer' : 'default',
                          opacity: entry.audioClip ? 1 : 0.3,
                        }}
                        disabled={!entry.audioClip}
                      >
                        <PlayPauseIcon isPlaying={isPlaying} size={9} playFill="rgba(255,255,255,0.85)" />
                      </button>
                    </div>

                    {/* Text info */}
                    <p className="text-sm font-medium text-center leading-tight mb-0.5 w-full" style={{ color: 'rgba(255,255,255,0.9)', fontStyle: entry.commonName ? 'normal' : 'italic' }}>{entry.commonName ?? entry.latinTaxa}</p>
                    {entry.commonName && <p className="text-xs text-center leading-tight w-full" style={{ color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>{entry.latinTaxa}</p>}
                    <div className="mt-auto pt-3 flex flex-col items-center">
                      <p className="text-lg font-bold leading-none mb-0.5" style={{ color: 'rgba(255,255,255,0.9)' }}>{entry.count.toLocaleString()}</p>
                      <p className="text-xs uppercase tracking-widest mb-2" style={{ color: 'rgba(255,255,255,0.3)', fontSize: 9 }}>{t[MyHiveLocalizationKey.BioacousticsDetections]}</p>
                      <p className="text-xs uppercase tracking-widest text-center" style={{ color: 'rgba(255,255,255,0.25)', fontSize: 9 }}>{t[MyHiveLocalizationKey.BioacousticsLastSeen]}</p>
                      <p className="text-xs text-center" style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10 }}>
                        {formatLocalDateTime(entry.mostRecentDate)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">

              {/* Left: when birds are most active */}
              <div className="rounded-sm p-5 flex flex-col gap-3" style={{ backgroundColor: 'rgba(255,255,255,0.92)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-0.5">{t[MyHiveLocalizationKey.BioacousticsDawnChorus]}</p>
                  <p className="text-xs uppercase tracking-widest text-gray-400">{t[MyHiveLocalizationKey.BioacousticsWhenBirdsSingAllTime]}</p>
                </div>
                <div style={{ height: 240 }}>
                  <BioacousticsHourlyRadarChart data={yearData} series="birds" showPeakLabel />
                </div>
                <p className="text-sm text-gray-600 leading-relaxed text-center">
                  {t[MyHiveLocalizationKey.BioacousticsPageDawnChorusDescPre]}<em style={{ color: 'var(--color-brand-teal-500)' }}>{t[MyHiveLocalizationKey.BioacousticsPageDawnChorusDescEmphasis]}</em>{t[MyHiveLocalizationKey.BioacousticsPageDawnChorusDescPost]}
                </p>
              </div>

              {/* Right: bird stats */}
              <div className="rounded-sm p-5 flex flex-col gap-6" style={{ backgroundColor: 'rgba(255,255,255,0.92)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <p className="text-sm font-medium text-gray-700">{t[MyHiveLocalizationKey.BioacousticsByTheNumbers]}</p>
                <div>
                  <HillTooltip
                    label={t[MyHiveLocalizationKey.BioacousticsBirdDiversityScore]}
                    description={t[MyHiveLocalizationKey.BioacousticsBirdDiversityScoreDescription]}
                    labelColor="text-gray-400"
                  />
                  <p className="text-5xl font-bold text-gray-900 mt-1">{birdStats.diversity}</p>
                  <p className="text-xs text-gray-400 mt-1">{t[MyHiveLocalizationKey.BioacousticsEffectiveSpeciesFromAllDetections]}</p>
                </div>
                <div className="flex gap-8">
                  <div>
                    <p className="text-4xl font-bold text-gray-900">{birdStats.species}</p>
                    <p className="text-xs uppercase tracking-widest text-gray-500 mt-1">{t[MyHiveLocalizationKey.BioacousticsPageUniqueSpecies]}</p>
                  </div>
                  <div>
                    <p className="text-4xl font-bold text-gray-900">{birdStats.detections}</p>
                    <p className="text-xs uppercase tracking-widest text-gray-500 mt-1">{t[MyHiveLocalizationKey.BioacousticsTotalDetections]}</p>
                  </div>
                </div>
                <BirdSpeciesBar data={yearData} />
              </div>

            </div>

            </>
          ) : (
            <div className="relative">

              {/* Ghost species strip */}
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-8 select-none pointer-events-none" style={{ filter: 'blur(5px)', opacity: 0.3 }}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex flex-col items-center rounded-sm px-3 py-5" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <div className="rounded-full w-16 h-16 mb-3" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }} />
                    <div className="h-3 w-20 rounded mb-2" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />
                    <div className="h-2.5 w-14 rounded mb-4" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
                    <div className="h-5 w-10 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />
                  </div>
                ))}
              </div>

              {/* Ghost chart row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 select-none pointer-events-none" style={{ filter: 'blur(5px)', opacity: 0.25 }}>
                <div className="rounded-sm" style={{ height: 290, backgroundColor: 'rgba(255,255,255,0.92)' }} />
                <div className="rounded-sm" style={{ height: 290, backgroundColor: 'rgba(255,255,255,0.92)' }} />
              </div>

              {/* Lock overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="rounded-sm px-8 py-7 text-center max-w-xs" style={{ backgroundColor: 'rgba(1,51,49,0.9)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)' }}>
                  <img src={birdSensorImage} alt="Bird sensor" className="w-20 h-20 object-cover rounded-full mx-auto mb-4" style={{ opacity: 0.75 }} />
                  <p className="text-sm font-medium mb-2" style={{ color: 'rgba(255,255,255,0.9)' }}>{t[MyHiveLocalizationKey.BioacousticsPageBirdSensorNotActive]}</p>
                  <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)' }}>{t[MyHiveLocalizationKey.BioacousticsPageBirdSensorDescription]}</p>
                </div>
              </div>

            </div>
          )}
        </div>
      </div>

      {/* 04 · Upsell */}
      {isSample &&
        <div className="border-t border-gray-300 bg-brand-soft-gray">
          <div className="max-w-3xl mx-auto px-4 sm:px-8 py-16 sm:py-24 flex flex-col items-center text-center">

            {/* Hero headline */}
            <h2 className="text-4xl sm:text-5xl font-inter text-gray-900 leading-tight mb-4">
              {t[MyHiveLocalizationKey.BioacousticsPageUpsellTitlePre]}<br />
              {t[MyHiveLocalizationKey.BioacousticsPageUpsellTitleMid]}<em>{t[MyHiveLocalizationKey.BioacousticsPageUpsellTitleYour]}</em>{t[MyHiveLocalizationKey.BioacousticsPageUpsellTitlePost]}
            </h2>
            <p className="text-sm text-gray-600 leading-relaxed max-w-xl mb-12">
              {t[MyHiveLocalizationKey.BioacousticsPageUpsellDescription]}
            </p>

            {/* Sensor image card */}
            <div className="w-full max-w-lg rounded-xl overflow-hidden shadow-md bg-white mb-12">
              <img src={pollinatorSensorImage} alt="The nature sensor" className="w-full object-cover" style={{ maxHeight: 220 }} />
              <div className="px-5 py-4 text-left">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2.5 h-2.5 rounded-full bg-brand-warm-orange-500 inline-block" />
                  <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: 'var(--color-brand-teal-500)' }} />
                  <span className="text-sm font-semibold text-gray-900">{t[MyHiveLocalizationKey.BioacousticsPageSensorName]}</span>
                </div>
                <p className="text-sm text-gray-500">{t[MyHiveLocalizationKey.BioacousticsPageSensorDescription]}</p>
              </div>
            </div>

            {/* Problem / Solution */}
            <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-8 text-left mb-12">
              <div>
                <div className="w-6 h-0.5 mb-3" style={{ backgroundColor: 'var(--color-brand-warm-orange-500)' }} />
                <p className="text-xs uppercase tracking-widest mb-3" style={{ color: 'var(--color-brand-warm-orange-500)' }}>{t[MyHiveLocalizationKey.BioacousticsPageTheProblem]}</p>
                <p className="text-sm text-gray-700 leading-relaxed">
                  <strong>{t[MyHiveLocalizationKey.BioacousticsPageProblemDescPre]}</strong>{t[MyHiveLocalizationKey.BioacousticsPageProblemDescPost]}
                </p>
              </div>
              <div>
                <div className="w-6 h-0.5 mb-3" style={{ backgroundColor: 'var(--color-brand-teal-500)' }} />
                <p className="text-xs uppercase tracking-widest mb-3" style={{ color: 'var(--color-brand-teal-500)' }}>{t[MyHiveLocalizationKey.BioacousticsPageTheSolution]}</p>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {t[MyHiveLocalizationKey.BioacousticsPageSolutionDescription]}
                </p>
              </div>
            </div>

            {/* Three ways */}
            <div className="w-full text-left mb-12">
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-4">{t[MyHiveLocalizationKey.BioacousticsPageThreeWaysTitle]}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                {[
                  {
                    n: '01',
                    title: t[MyHiveLocalizationKey.BioacousticsPageThreeWaysOnePlatformTitle],
                    body: t[MyHiveLocalizationKey.BioacousticsPageThreeWaysOnePlatformBody],
                  },
                  {
                    n: '02',
                    title: t[MyHiveLocalizationKey.BioacousticsPageThreeWaysOnlyDataTitle],
                    body: t[MyHiveLocalizationKey.BioacousticsPageThreeWaysOnlyDataBody],
                  },
                  {
                    n: '03',
                    title: t[MyHiveLocalizationKey.BioacousticsPageThreeWaysDDQsTitle],
                    body: t[MyHiveLocalizationKey.BioacousticsPageThreeWaysDDQsBody],
                  },
                ].map(({ n, title, body }) => (
                  <div key={n}>
                    <div className="w-full h-px bg-gray-400 mb-4" />
                    <p className="text-xs text-gray-400 mb-2">{n}</p>
                    <p className="text-sm font-semibold text-gray-900 mb-2">{title}</p>
                    <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* WSJ */}
            <div className="w-full h-px bg-gray-400 mb-10" />
            <p className="text-xs uppercase tracking-widest text-gray-400 mb-5">{t[MyHiveLocalizationKey.BioacousticsPageAsFeaturedIn]}</p>
            <a href="https://www.wsj.com/articles/the-empire-state-building-is-tracking-the-buzzing-of-nearby-bees-bd72de6c?eafs_enabled=false" target="_blank" rel="noopener noreferrer">
              <img src={wsjLogo} alt="The Wall Street Journal" className="h-9 w-auto mb-6" />
            </a>
            <p className="text-sm text-gray-500">{t[MyHiveLocalizationKey.BioacousticsPageDeployedAtPre]}<strong>{t[MyHiveLocalizationKey.BioacousticsPageDeployedAtBrand]}</strong>.</p>
            <div className="w-full h-px bg-gray-400 mt-10 mb-8" />

            {/* Pricing card */}
            <div className="w-full rounded-xl p-8 mb-4 flex flex-col sm:flex-row gap-8" style={{ backgroundColor: 'var(--color-brand-teal-dark-500)' }}>
              <div className="flex-1">
                <p className="text-white leading-none mb-2">
                  <span className="text-5xl font-bold">$4,900</span>
                  <span className="text-base font-normal ml-2 opacity-70">{t[MyHiveLocalizationKey.BioacousticsPagePerYearPerProperty]}</span>
                </p>
                <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>{t[MyHiveLocalizationKey.BioacousticsPagePricingNote]}</p>
                <a href="https://aura.alveole.com/book-a-demo" target="_blank" rel="noopener noreferrer" className="inline-block px-6 py-3 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: 'var(--color-brand-warm-orange-500)' }}>
                  {t[MyHiveLocalizationKey.GetStarted]}
                </a>
              </div>
              <div className="sm:border-l sm:pl-8" style={{ borderColor: 'rgba(255,255,255,0.12)' }}>
                <p className="text-xs uppercase tracking-widest mb-4" style={{ color: 'rgba(255,255,255,0.45)' }}>{t[MyHiveLocalizationKey.BioacousticsPageWhatsIncluded]}</p>
                <ul className="flex flex-col gap-2.5">
                  {[
                    t[MyHiveLocalizationKey.BioacousticsPageIncluded1],
                    t[MyHiveLocalizationKey.BioacousticsPageIncluded2],
                    t[MyHiveLocalizationKey.BioacousticsPageIncluded3],
                    t[MyHiveLocalizationKey.BioacousticsPageIncluded4],
                  ].map(item => (
                    <li key={item} className="flex items-start gap-2.5 text-sm" style={{ color: 'rgba(255,255,255,0.85)' }}>
                      <svg className="shrink-0 mt-0.5" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8l3.5 3.5L13 5" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Free tool card */}
            <div className="w-full flex flex-col items-center gap-4 py-6">
              <p className="text-2xl font-bold text-gray-700">{t[MyHiveLocalizationKey.BioacousticsPageFreeScreeningTitle]}</p>
              <BioacousticsSearchBar />
              <p className="text-xs text-gray-400">{t[MyHiveLocalizationKey.BioacousticsPageTryItFree]}</p>
            </div>

          </div>
        </div>
      }
    </div>
  );
}
